import { useEffect, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import { Tables } from "@/integrations/supabase/types";
import { Navigate } from "react-router-dom";
import { RefreshCw, Users, Mail, Activity, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

interface TenantMetrics {
  totalTenants: number;
  totalMailboxes: number;
  totalRules: number;
  totalEvents: number;
  failedEvents: number;
}

interface TenantInfo {
  tenant_id: string;
  company_name: string;
  created_at: string;
  mailbox_count: number;
  rule_count: number;
  recent_events: number;
}

export default function OpsDashboardPage() {
  const { user, tenantType } = useAuth();
  const [metrics, setMetrics] = useState<TenantMetrics | null>(null);
  const [tenants, setTenants] = useState<TenantInfo[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  // Redirect non-admin users
  if (tenantType !== "admin") {
    return <Navigate to="/dashboard" replace />;
  }

  const fetchDashboardData = async () => {
    try {
      setRefreshing(true);
      
      // Fetch customers (tenants)
      const { data: customers, error: customersError } = await supabase
        .from("customers")
        .select("*");

      if (customersError) throw customersError;

      // Fetch all mailboxes 
      const { data: mailboxes, error: mailboxesError } = await supabase
        .from("mailboxes")
        .select("tenant_id");

      if (mailboxesError) throw mailboxesError;

      // Fetch all rules
      const { data: rules, error: rulesError } = await supabase
        .from("rules")
        .select("tenant_id");

      if (rulesError) throw rulesError;

      // Fetch recent events (last 24 hours)
      const { data: events, error: eventsError } = await supabase
        .from("email_events")
        .select("tenant_id, status")
        .gte("created_at", new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString());

      if (eventsError) throw eventsError;

      // Calculate metrics
      const totalTenants = customers?.length || 0;
      const totalMailboxes = mailboxes?.length || 0;
      const totalRules = rules?.length || 0;
      const totalEvents = events?.length || 0;
      const failedEvents = events?.filter(e => e.status === "faulted").length || 0;

      setMetrics({
        totalTenants,
        totalMailboxes,
        totalRules,
        totalEvents,
        failedEvents
      });

      // Process tenant info
      const tenantInfo: TenantInfo[] = (customers || []).map(customer => {
        const tenantMailboxes = mailboxes?.filter(m => m.tenant_id === customer.tenant_id).length || 0;
        const tenantRules = rules?.filter(r => r.tenant_id === customer.tenant_id).length || 0;
        const recentEvents = events?.filter(e => e.tenant_id === customer.tenant_id).length || 0;

        return {
          tenant_id: customer.tenant_id,
          company_name: customer.company_name,
          created_at: customer.created_at,
          mailbox_count: tenantMailboxes,
          rule_count: tenantRules,
          recent_events: recentEvents
        };
      });

      setTenants(tenantInfo);
    } catch (error) {
      console.error("Error fetching dashboard data:", error);
      toast.error("Failed to load dashboard data");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const handleRefresh = () => {
    fetchDashboardData();
  };

  if (loading) {
    return (
      <div className="flex h-screen w-full items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">System Dashboard</h1>
          <p className="text-muted-foreground">Internal operator portal - cross-tenant overview</p>
        </div>
        <Button onClick={handleRefresh} disabled={refreshing}>
          <RefreshCw className={`h-4 w-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* Metrics Overview */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Tenants</CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{metrics?.totalTenants || 0}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Mailboxes</CardTitle>
            <Mail className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{metrics?.totalMailboxes || 0}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Rules</CardTitle>
            <Activity className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{metrics?.totalRules || 0}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Events (24h)</CardTitle>
            <Activity className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{metrics?.totalEvents || 0}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Failed Events</CardTitle>
            <AlertTriangle className="h-4 w-4 text-destructive" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-destructive">{metrics?.failedEvents || 0}</div>
          </CardContent>
        </Card>
      </div>

      {/* Tenant Listing */}
      <Card>
        <CardHeader>
          <CardTitle>Tenant Overview</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {tenants.length === 0 ? (
              <p className="text-center text-muted-foreground py-8">No tenants found</p>
            ) : (
              tenants.map((tenant) => (
                <div key={tenant.tenant_id} className="flex items-center justify-between p-4 border rounded-lg">
                  <div className="space-y-1">
                    <div className="flex items-center gap-2">
                      <h3 className="font-medium">{tenant.company_name}</h3>
                      <Badge variant="outline" className="text-xs">
                        {tenant.tenant_id}
                      </Badge>
                    </div>
                    <p className="text-sm text-muted-foreground">
                      Created: {new Date(tenant.created_at).toLocaleDateString()}
                    </p>
                  </div>
                  <div className="flex items-center gap-4 text-sm">
                    <div className="text-center">
                      <div className="font-medium">{tenant.mailbox_count}</div>
                      <div className="text-muted-foreground">Mailboxes</div>
                    </div>
                    <div className="text-center">
                      <div className="font-medium">{tenant.rule_count}</div>
                      <div className="text-muted-foreground">Rules</div>
                    </div>
                    <div className="text-center">
                      <div className="font-medium">{tenant.recent_events}</div>
                      <div className="text-muted-foreground">Events (24h)</div>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}